import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface UseInventoryManagementProps {
  onInventoryComplete: () => void;
  onInventoryStart: () => void;
  stockItems: any[];
}

export const useInventoryManagement = ({
  onInventoryComplete,
  onInventoryStart,
  stockItems,
}: UseInventoryManagementProps) => {
  const [isActive, setIsActive] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isValidationOpen, setIsValidationOpen] = useState(false);
  const [inventoryData, setInventoryData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const handleStartInventory = () => {
    setIsActive(true);
    onInventoryStart();
    toast.success("Mode inventaire activé");
  };

  const handleCancelInventory = () => {
    setIsActive(false);
    setInventoryData([]);
    onInventoryComplete();
    toast.info("Mode inventaire annulé");
  };

  const handleItemClick = (item: any) => {
    setSelectedItem(item);
    setIsDialogOpen(true);
  };

  const handleConfirmCount = (countedQuantity: number, notes: string) => {
    setInventoryData([
      ...inventoryData,
      {
        itemId: selectedItem.id,
        name: selectedItem.data?.name,
        systemQuantity: selectedItem.quantity,
        countedQuantity,
        difference: countedQuantity - selectedItem.quantity,
        notes
      }
    ]);
    setIsDialogOpen(false);
    toast.success(`Comptage enregistré pour ${selectedItem.data?.name || 'le produit'}`);
  };

  const handleFinishInventory = () => {
    setIsValidationOpen(true);
  };

  const handleValidateInventory = async () => {
    try {
      const { data: validationRecord, error: validationError } = await supabase
        .from('inventory_validations')
        .insert([{
          inventory_data: inventoryData,
          status: 'validated',
          validated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (validationError) throw validationError;

      for (const item of inventoryData) {
        const { error: updateError } = await supabase
          .from('stock_items')
          .update({ quantity: item.countedQuantity })
          .eq('id', item.itemId);

        if (updateError) throw updateError;
      }

      setIsActive(false);
      setInventoryData([]);
      setIsValidationOpen(false);
      onInventoryComplete();
      toast.success("Inventaire terminé avec succès");
    } catch (error: any) {
      console.error("Erreur lors de la validation:", error);
      toast.error("Erreur lors de la validation de l'inventaire");
    }
  };

  // Filter and pagination logic
  const filteredItems = stockItems.filter(item => 
    item.data?.name?.toLowerCase().includes(searchQuery.toLowerCase()) || false
  );

  const itemsPerPage = 12;
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);

  const progress = (inventoryData.length / stockItems.length) * 100;

  return {
    isActive,
    selectedItem,
    isDialogOpen,
    setIsDialogOpen,
    isValidationOpen,
    setIsValidationOpen,
    inventoryData,
    searchQuery,
    setSearchQuery,
    currentPage,
    setCurrentPage,
    progress,
    currentItems,
    totalPages,
    handleStartInventory,
    handleCancelInventory,
    handleItemClick,
    handleConfirmCount,
    handleFinishInventory,
    handleValidateInventory,
  };
};